import { createMemo, createSignal, For, Show } from "solid-js";
import Text from "./Text";
import Modal from "./Modal";
import { Badge } from "./Badge";
import { SearchInput } from "./SearchInput";
import { useTranslation } from "../hooks/useTranslation";
import type { EventDefinitionDto } from "../api/generated/schemas";
import styles from "./EventPicker.module.css";

// Order the sections appear in, most reached for first. Anything with a category not listed here
// still shows, after these.
const CATEGORY_ORDER = ["bot", "economy", "chat", "tasks", "world", "protection"];

interface Props {
    events: readonly EventDefinitionDto[];
    value: string;
    onChange: (eventKey: string) => void;
}

export default function EventPicker(props: Props) {
    const { t } = useTranslation();

    const [open, setOpen] = createSignal(false);
    const [query, setQuery] = createSignal("");

    const selected = () => props.events.find(e => e.key === props.value);

    const label = (event: EventDefinitionDto) => t(`EVENT_${event.key.toUpperCase()}`);

    // Parameter names are searchable too: people look for the value they want to react to ("payer")
    // more often than they remember what the event is called.
    const matches = createMemo(() => {
        const needle = query().trim().toLowerCase();
        if (needle === "") return props.events;

        return props.events.filter(event =>
            label(event).toLowerCase().includes(needle)
            || event.key.toLowerCase().includes(needle)
            || (event.plugin ?? "").toLowerCase().includes(needle)
            || event.parameters.some(p => p.key.toLowerCase().includes(needle)));
    });

    const grouped = createMemo(() => {
        const byCategory = new Map<string, EventDefinitionDto[]>();
        for (const event of matches()) {
            const bucket = byCategory.get(event.category) ?? [];
            bucket.push(event);
            byCategory.set(event.category, bucket);
        }

        return [...byCategory.entries()]
            .sort(([a], [b]) => {
                const rank = (c: string) => {
                    const index = CATEGORY_ORDER.indexOf(c);
                    return index < 0 ? CATEGORY_ORDER.length : index;
                };
                return rank(a) - rank(b) || a.localeCompare(b);
            })
            .map(([category, events]) => ({
                category,
                events: events.sort((a, b) => label(a).localeCompare(label(b))),
            }));
    });

    const choose = (eventKey: string) => {
        props.onChange(eventKey);
        setOpen(false);
        setQuery("");
    };

    return (
        <>
            <button type="button" class={styles.trigger} onClick={() => setOpen(true)}>
                <span class={styles.triggerLabel}>
                    <Show when={selected()} fallback={<span class={styles.triggerEmpty}><Text val="AUTOMATION_EVENT_PICK" /></span>}>
                        {(event) => (
                            <>
                                <span>{label(event())}</span>
                                <Badge tone="neutral" pill>{t(`EVENT_CATEGORY_${event().category.toUpperCase()}`)}</Badge>
                            </>
                        )}
                    </Show>
                </span>
                <i class="fa-solid fa-chevron-down" />
            </button>

            <Modal open={open()} onClose={() => setOpen(false)} title={t("AUTOMATION_EVENT_PICK")} maxWidth="40rem">
                <div class={styles.panel}>
                    <SearchInput
                        value={query()}
                        onInput={setQuery}
                        placeholder={t("AUTOMATION_EVENT_SEARCH")}
                        ariaLabel={t("AUTOMATION_EVENT_SEARCH")}
                        autofocus
                    />

                    <Show
                        when={grouped().length > 0}
                        fallback={
                            <div class={styles.empty}>
                                <i class="fa-solid fa-magnifying-glass" style={{ "font-size": "3rem", opacity: 0.5 }} />
                                <p><Text val="AUTOMATION_EVENT_NO_MATCH" /></p>
                            </div>
                        }
                    >
                        <div class={styles.results}>
                            <For each={grouped()}>
                                {(section) => (
                                    <div class={styles.category}>
                                        <span class={styles.categoryLabel}>
                                            {t(`EVENT_CATEGORY_${section.category.toUpperCase()}`)}
                                        </span>
                                        <For each={section.events}>
                                            {(event) => (
                                                <button
                                                    type="button"
                                                    class={`${styles.option} ${event.key === props.value ? styles.optionSelected : ""}`}
                                                    onClick={() => choose(event.key)}
                                                >
                                                    <span class={styles.optionText}>
                                                        <span>{label(event)}</span>
                                                        <Show when={event.parameters.length > 0}>
                                                            <span class={styles.optionParams}>
                                                                {event.parameters.map(p => p.key).join(", ")}
                                                            </span>
                                                        </Show>
                                                    </span>
                                                    <span class={styles.optionBadges}>
                                                        <Show when={event.observeOnly}>
                                                            <Badge tone="info" pill><Text val="AUTOMATION_EVENT_OBSERVE_BADGE" /></Badge>
                                                        </Show>
                                                        <Show when={!event.pluginEnabled}>
                                                            <Badge tone="warning" pill><Text val="AUTOMATION_EVENT_OFF_BADGE" /></Badge>
                                                        </Show>
                                                    </span>
                                                </button>
                                            )}
                                        </For>
                                    </div>
                                )}
                            </For>
                        </div>
                    </Show>
                </div>
            </Modal>
        </>
    );
}
